import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  TextField,
  MenuItem,
  Typography,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import DiceBearAvatar from './DiceBearAvatar';
import { mapLegacyConfigToDicebear, mapDicebearToLegacyConfig, type LegacyAvatarConfig } from '../../utils/avatarMapping';

interface AvatarCustomizerProps {
  open: boolean;
  onClose: () => void;
  initialConfig?: Record<string, any> | null;
  onSave: (config: Record<string, any>) => Promise<void>;
  userName: string;
}

interface AvatarConfig {
  top?: string[];
  eyes?: string[];
  mouth?: string[];
  accessories?: string[];
  clothing?: string[];
  hairColor?: string[];
  skinColor?: string[];
  clothesColor?: string[];
  backgroundColor?: string[];
  backgroundType?: ('solid' | 'gradientLinear')[];
}

const HAIR_STYLE_OPTIONS = [
  { value: 'shortFlat', label: 'สั้นเรียบ' },
  { value: 'shortRound', label: 'สั้นกลม' },
  { value: 'curly', label: 'หยิก' },
  { value: 'curvy', label: 'หนา' },
  { value: 'longButNotTooLong', label: 'ยาวปานกลาง' },
  { value: 'straight02', label: 'ยาวตรง' },
  { value: 'bob', label: 'บ๊อบ' },
  { value: 'miaWallace', label: 'เมีย วอลเลซ' },
  { value: 'shaggyMullet', label: 'โมฮอว์ก' },
  { value: 'turban', label: 'เทอร์แบน' },
  { value: 'winterHat01', label: 'บีนนี่' },
];

const EYE_STYLE_OPTIONS = [
  { value: 'default', label: 'ปกติ' },
  { value: 'happy', label: 'ยิ้ม' },
  { value: 'squint', label: 'รี' },
  { value: 'surprised', label: 'ประหลาดใจ' },
  { value: 'wink', label: 'หลับข้าง' },
];

const MOUTH_STYLE_OPTIONS = [
  { value: 'smile', label: 'ยิ้ม' },
  { value: 'serious', label: 'สงบ' },
  { value: 'concerned', label: 'กังวล' },
  { value: 'laugh', label: 'หัวเราะ' },
];

const ACCESSORIES_OPTIONS = [
  { value: '', label: 'ไม่มี' },
  { value: 'round', label: 'กลม' },
  { value: 'prescription02', label: 'เหลี่ยม' },
  { value: 'sunglasses', label: 'กันแดด' },
];

const CLOTHING_OPTIONS = [
  { value: 'hoodie', label: 'ฮู้ด' },
  { value: 'shirtCrewNeck', label: 'คอกลม' },
  { value: 'shirtScoopNeck', label: 'คอโค้ง' },
  { value: 'collarAndSweater', label: 'โปโล' },
  { value: 'blazerAndShirt', label: 'เบลเซอร์' },
];

const COLOR_OPTIONS = [
  '#2c1b18', '#4a312c', '#724133', '#a55728', '#b58143',
  '#c93305', '#d6b370', '#e8e1e1', '#ecdcbf', '#f59797',
];

const SKIN_COLOR_OPTIONS = [
  '#614335', '#ae5d29', '#d08b5b', '#edb98a', '#f8d25c', '#fd9841', '#ffdbb4',
];

const BG_COLOR_OPTIONS = [
  '#b6e3f4', '#c0aede', '#d1d4f9', '#ffd5dc', '#ffdfbf', '#65c9ff',
];

function getRandomConfig(): AvatarConfig {
  const randomIndex = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];
  
  return {
    top: [randomIndex(['shortFlat', 'curly', 'bob', 'longButNotTooLong', 'straight02'])],
    eyes: [randomIndex(['default', 'happy', 'squint'])],
    mouth: [randomIndex(['smile', 'serious', 'concerned'])],
    clothing: [randomIndex(['hoodie', 'shirtCrewNeck', 'collarAndSweater'])],
    hairColor: [randomIndex(COLOR_OPTIONS)],
    skinColor: [randomIndex(SKIN_COLOR_OPTIONS)],
    clothesColor: [randomIndex(COLOR_OPTIONS)],
    backgroundColor: [randomIndex(BG_COLOR_OPTIONS)],
    backgroundType: ['solid'],
  };
}

function ColorSwatchField({
  label,
  value,
  onChange,
  colors,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  colors: string[];
}) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {colors.map((color) => (
          <Box
            key={color}
            onClick={() => onChange(color)}
            sx={{
              width: 22,
              height: 22,
              borderRadius: '50%',
              bgcolor: color,
              cursor: 'pointer',
              border: value === color ? '2px solid #1976d2' : '2px solid transparent',
              boxSizing: 'border-box',
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

const AvatarCustomizer: React.FC<AvatarCustomizerProps> = ({
  open,
  onClose,
  initialConfig,
  onSave,
  userName,
}) => {
  const [dicebearConfig, setDicebearConfig] = useState<AvatarConfig>(() => {
    if (initialConfig && Object.keys(initialConfig).length > 0) {
      return mapLegacyConfigToDicebear(initialConfig as LegacyAvatarConfig);
    }
    return getRandomConfig();
  });
  
  const [saving, setSaving] = useState(false);

  const update = (key: string, value: any) => {
    if (key === 'accessories' && !value) {
      setDicebearConfig((prev: AvatarConfig) => ({ ...prev, accessories: [] }));
    } else {
      setDicebearConfig((prev: AvatarConfig) => ({ ...prev, [key]: Array.isArray(value) ? value : [value] }));
    }
  };

  const handleRandomise = () => {
    setDicebearConfig(getRandomConfig());
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const legacyConfig = mapDicebearToLegacyConfig(dicebearConfig);
      await onSave(legacyConfig as Record<string, any>);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>ปรับแต่งอวตาร</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5,
              minWidth: 140,
            }}
          >
            <DiceBearAvatar
              seed={userName}
              config={dicebearConfig}
              size={120}
            />
            <Typography variant="caption" color="text.secondary">
              {userName}
            </Typography>
            <Button size="small" variant="outlined" onClick={handleRandomise}>
              สุ่มใหม่
            </Button>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select fullWidth size="small" label="ทรงผม"
                  value={dicebearConfig.top?.[0] || 'shortFlat'}
                  onChange={(e) => update('top', e.target.value)}
                >
                  {HAIR_STYLE_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select fullWidth size="small" label="ดวงตา"
                  value={dicebearConfig.eyes?.[0] || 'default'}
                  onChange={(e) => update('eyes', e.target.value)}
                >
                  {EYE_STYLE_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select fullWidth size="small" label="ปาก"
                  value={dicebearConfig.mouth?.[0] || 'smile'}
                  onChange={(e) => update('mouth', e.target.value)}
                >
                  {MOUTH_STYLE_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select fullWidth size="small" label="แว่นตา"
                  value={dicebearConfig.accessories?.[0] || ''}
                  onChange={(e) => update('accessories', e.target.value)}
                >
                  {ACCESSORIES_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select fullWidth size="small" label="เสื้อ"
                  value={dicebearConfig.clothing?.[0] || 'hoodie'}
                  onChange={(e) => update('clothing', e.target.value)}
                >
                  {CLOTHING_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={dicebearConfig.backgroundType?.[0] === 'gradientLinear'}
                      onChange={(e) => update('backgroundType', e.target.checked ? ['gradientLinear'] : ['solid'])}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">พื้นหลังไล่สี</Typography>}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ColorSwatchField
                  label="สีผม"
                  value={dicebearConfig.hairColor?.[0] || '#2c1b18'}
                  onChange={(v) => update('hairColor', v)}
                  colors={COLOR_OPTIONS}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ColorSwatchField
                  label="สีผิว"
                  value={dicebearConfig.skinColor?.[0] || '#ffdbb4'}
                  onChange={(v) => update('skinColor', v)}
                  colors={SKIN_COLOR_OPTIONS}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ColorSwatchField
                  label="สีเสื้อ"
                  value={dicebearConfig.clothesColor?.[0] || '#3c4f5c'}
                  onChange={(v) => update('clothesColor', v)}
                  colors={COLOR_OPTIONS}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ColorSwatchField
                  label="สีพื้นหลัง"
                  value={dicebearConfig.backgroundColor?.[0] || '#b6e3f4'}
                  onChange={(v) => update('backgroundColor', v)}
                  colors={BG_COLOR_OPTIONS}
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={saving}>
          ยกเลิก
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {saving ? 'กำลังบันทึก...' : 'บันทึก'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AvatarCustomizer;
